"use client";

import { useTransition } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Pin, PinOff, Trash2, RotateCcw, Tag as TagIcon } from "lucide-react";
import {
  softDeleteNote,
  updateNote,
  restoreNote,
  deleteNoteForever,
} from "@/app/actions";
import type { NoteWithTags } from "@/lib/queries";
import { type NoteColor, noteColorClass, resolveColor } from "@/lib/colors";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ColorPicker } from "@/components/notes/color-picker";

export function NoteCard({
  note,
  onOpen,
  variant = "active",
}: {
  note: NoteWithTags;
  onOpen?: (note: NoteWithTags) => void;
  variant?: "active" | "trash";
}) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();

  function run(fn: () => Promise<{ ok: boolean; error?: string }>, success?: string) {
    startTransition(async () => {
      const res = await fn();
      if (!res.ok) {
        toast.error(res.error ?? "Algo salió mal");
        return;
      }
      router.refresh();
      if (success) toast.success(success);
    });
  }

  const isTrash = variant === "trash";

  return (
    <article
      className={cn(
        "group/note relative flex flex-col gap-3 rounded-2xl p-4 ring-1 ring-foreground/10 shadow-sm transition-all",
        !isTrash && "cursor-text hover:shadow-md hover:ring-foreground/20",
        noteColorClass(note.color),
        pending && "opacity-60",
      )}
      onClick={isTrash ? undefined : () => onOpen?.(note)}
    >
      {note.pinned && !isTrash && (
        <Pin className="absolute right-3 top-3 size-3.5 fill-current text-primary" />
      )}

      {note.title && (
        <h3 className="pr-5 font-display text-lg leading-tight font-medium text-foreground">
          {note.title}
        </h3>
      )}

      {note.content && (
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/80 line-clamp-[12]">
          {note.content}
        </p>
      )}

      {note.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {note.tags.map((t) => (
            <Badge key={t.id} variant="secondary" className="gap-1 bg-foreground/5 text-foreground/70">
              <TagIcon className="size-2.5" />
              {t.name}
            </Badge>
          ))}
        </div>
      )}

      <footer className="mt-auto flex items-center justify-between pt-1">
        <time className="text-xs text-muted-foreground" dateTime={new Date(note.updatedAt).toISOString()}>
          {isTrash && note.deletedAt
            ? `Eliminada ${formatDate(note.deletedAt)}`
            : formatDate(note.updatedAt)}
        </time>

        <div
          className={cn(
            "flex items-center gap-0.5 transition-opacity",
            !isTrash && "opacity-0 group-hover/note:opacity-100 group-focus-within/note:opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {isTrash ? (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Restaurar nota"
                title="Restaurar"
                onClick={() => run(() => restoreNote(note.id), "Nota restaurada")}
              >
                <RotateCcw />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Eliminar definitivamente"
                title="Eliminar definitivamente"
                className="text-destructive hover:text-destructive"
                onClick={() => run(() => deleteNoteForever(note.id), "Eliminada para siempre")}
              >
                <Trash2 />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={note.pinned ? "Quitar fijado" : "Fijar nota"}
                title={note.pinned ? "Quitar fijado" : "Fijar"}
                className={cn(note.pinned && "text-primary")}
                onClick={() => run(() => updateNote({ id: note.id, pinned: !note.pinned }))}
              >
                {note.pinned ? <Pin className="fill-current" /> : <PinOff />}
              </Button>
              <ColorPicker
                value={resolveColor(note.color)}
                onChange={(color: NoteColor) => run(() => updateNote({ id: note.id, color }))}
                align="end"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Mover a la papelera"
                title="Mover a la papelera"
                onClick={() => run(() => softDeleteNote(note.id), "Movida a la papelera")}
              >
                <Trash2 />
              </Button>
            </>
          )}
        </div>
      </footer>
    </article>
  );
}
